import React from "react";
import Button from "../components/Button";
import useBatchConfig, { IBatchConfigForm } from "../hooks/logics/batches/use-batch-config";
import { BsLayersFill, BsClockFill, BsGeoAltFill, BsPencilSquare, BsCheckLg, BsXCircle } from "react-icons/bs";

const ConfigField = ({
  label,
  description,
  unit,
  fieldKey,
  value,
  isEditing,
  onChange,
  step = 1,
  min = 0,
}: {
  label: string;
  description: string;
  unit: string;
  fieldKey: keyof IBatchConfigForm;
  value: number;
  isEditing: boolean;
  onChange: (field: keyof IBatchConfigForm, value: number) => void;
  step?: number;
  min?: number;
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50/70 hover:bg-gray-50 rounded-xl border border-gray-100 transition-all gap-4">
      <div className="flex flex-col gap-1 max-w-xl">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800 text-sm md:text-base">{label}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700 font-medium">
            {unit}
          </span>
        </div>
        <p className="text-xs text-gray-500">{description}</p>
        <span className="text-[11px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded w-fit mt-0.5">
          {fieldKey}
        </span>
      </div>

      <div className="min-w-[140px] flex justify-end">
        {isEditing ? (
          <div className="flex items-center gap-2 w-full md:w-36">
            <input
              type="number"
              step={step}
              min={min}
              value={value}
              onChange={(e) => onChange(fieldKey, parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-1.5 border border-primary/40 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white font-medium text-gray-900"
            />
          </div>
        ) : (
          <div className="px-4 py-2 bg-white rounded-lg border border-gray-200 shadow-2xs font-bold text-gray-900 text-sm md:text-base">
            {value} <span className="text-xs font-normal text-gray-500">{unit}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const BatchConfig = () => {
  const {
    loading,
    isUpdating,
    isEditing,
    setIsEditing,
    formData,
    handleChange,
    handleSubmit,
  } = useBatchConfig();

  return (
    <div className="w-full h-full min-h-screen py-6 md:py-10 bg-gray-100 font-inter">
      <div className="flex flex-col gap-6 max-w-7xl mx-auto">
        {/* Header Card */}
        <div className="w-full bg-white rounded-xl shadow-xs border border-gray-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                <BsLayersFill fontSize="1.5rem" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                  Batch Configurations
                </h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                  Manage global batch limits, capacity rules, time windows, and route radius settings.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  <BsXCircle /> Cancel
                </button>

                <Button
                  onClick={handleSubmit}
                  disabled={isUpdating}
                  className="flex items-center gap-2 min-h-0 h-10 px-5 text-sm"
                >
                  <BsCheckLg /> {isUpdating ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 min-h-0 h-10 px-5 text-sm"
              >
                <BsPencilSquare /> Edit Configurations
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="w-full flex items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
            <div className="loader"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Category 1: Capacity & Stops */}
            <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-primary font-bold text-base">
                <BsLayersFill />
                <h2>Batch Capacity & Stop Limits</h2>
              </div>

              <div className="flex flex-col gap-3">
                <ConfigField
                  label="Max Pickup Stops per Batch"
                  description="Maximum number of unique merchant pickup locations allowed in a single batch."
                  unit="stops"
                  fieldKey="MAX_PICKUP_STOPS"
                  value={formData.MAX_PICKUP_STOPS}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={1}
                />

                <ConfigField
                  label="Max Delivery Stops per Batch"
                  description="Maximum number of customer dropoff stops allowed in a single batch."
                  unit="stops"
                  fieldKey="MAX_DELIVERY_STOPS"
                  value={formData.MAX_DELIVERY_STOPS}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={1}
                />

                <ConfigField
                  label="Same Merchant Pickup Location Threshold"
                  description="Radius threshold in miles (~0.05 mi ≈ 80 meters) for orders from the same merchant to share one pickup stop."
                  unit="miles"
                  fieldKey="SAME_PICKUP_LOCATION_THRESHOLD_MILES"
                  value={formData.SAME_PICKUP_LOCATION_THRESHOLD_MILES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  step={0.01}
                  min={0.01}
                />
              </div>
            </div>

            {/* Category 2: Time Windows & Timers */}
            <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-primary font-bold text-base">
                <BsClockFill />
                <h2>Time Windows & Timers</h2>
              </div>

              <div className="flex flex-col gap-3">
                <ConfigField
                  label="Batch Forming Window"
                  description="Duration in minutes how long a batch stays in forming window accepting new orders before closing."
                  unit="minutes"
                  fieldKey="BATCH_WINDOW_MINUTES"
                  value={formData.BATCH_WINDOW_MINUTES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={0}
                />

                <ConfigField
                  label="Scheduled Dispatch Lead Time"
                  description="Minutes before an order's scheduleTime when a scheduled batch closes immediately."
                  unit="minutes"
                  fieldKey="SCHEDULED_DISPATCH_LEAD_MINUTES"
                  value={formData.SCHEDULED_DISPATCH_LEAD_MINUTES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={0}
                />

                <ConfigField
                  label="Max Scheduled Order Window"
                  description="Maximum allowed difference in minutes between scheduled order times to join the same batch."
                  unit="minutes"
                  fieldKey="MAX_SCHEDULE_WINDOW_MINUTES"
                  value={formData.MAX_SCHEDULE_WINDOW_MINUTES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={0}
                />

                <ConfigField
                  label="Unassigned Driver Timeout"
                  description="Timeout duration in minutes for unassigned dispatched batches before expanding driver search radius."
                  unit="minutes"
                  fieldKey="UNASSIGNED_TIMEOUT_MINUTES"
                  value={formData.UNASSIGNED_TIMEOUT_MINUTES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  min={0}
                />
              </div>
            </div>

            {/* Category 3: Distance & Route Boundaries */}
            <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-primary font-bold text-base">
                <BsGeoAltFill />
                <h2>Distance & Route Boundaries</h2>
              </div>

              <div className="flex flex-col gap-3">
                <ConfigField
                  label="Cross-Merchant Pickup Radius"
                  description="Maximum distance radius in miles allowed between different merchant pickup locations to group into one batch."
                  unit="miles"
                  fieldKey="MAX_PICKUP_RADIUS_MILES"
                  value={formData.MAX_PICKUP_RADIUS_MILES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  step={0.1}
                  min={0.1}
                />

                <ConfigField
                  label="Max Total Route Distance"
                  description="Maximum allowed total route distance in miles from the first pickup to the final dropoff stop."
                  unit="miles"
                  fieldKey="MAX_TOTAL_ROUTE_MILES"
                  value={formData.MAX_TOTAL_ROUTE_MILES}
                  isEditing={isEditing}
                  onChange={handleChange}
                  step={1}
                  min={1}
                />
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BatchConfig;